// CLIENTE DEL ESPEJO Solar Hub ⇄ FluxAI.
//
// Contraparte de `api/flux.js`. Existe para que sumar una capacidad de FluxAI sea declararla,
// no construir otra tubería: un consumidor nuevo llama `pedirAFlux('lo-que-sea', {...})` y ya
// hereda el canal directo y la degradación .
//
// CASCADA, en este orden (único canal):
//   1. BACKEND PROPIO — `/hub-api/flux` (Express en Railway, `server/routes/flux.js`). Es el
//      ÚNICO camino que alcanza a FluxAI por la RED INTERNA de Railway.
//   2. FUNCIÓN DE VERCEL — `/api/flux`, respaldo si el backend propio no tiene el espejo.
//
// Se baja un escalón solo cuando el canal NO EXISTE (503 = sin token, 404 = no desplegado).

import { hubApiActivo, hubApiBaseUrl } from './hub';

// Una vez que un canal contesta 503 ("no configurado") o 404 ("no desplegado"), no tiene sentido
// reintentarlo en cada consulta: la env var no va a aparecer a mitad de sesión.
let directoDisponible = true;   // función de Vercel
let porElHubDisponible = true;  // backend propio

/** Para pruebas y cambios de sesión: vuelve a considerar disponibles los canales directos. */
export function reiniciarCanalFlux() { directoDisponible = true; porElHubDisponible = true; }

/** `true` mientras el canal directo (Vercel) no se haya descartado en esta sesión. */
export const canalDirectoDisponible = () => directoDisponible;

/** `true` mientras el espejo por el backend propio no se haya descartado en esta sesión. */
export const canalHubDisponible = () => porElHubDisponible;

/**
 * Un intento contra UN espejo. Distingue tres desenlaces, y la distinción es el punto:
 *   · `{ hayCanal: false }`      → este espejo no existe acá; que pruebe el siguiente escalón.
 *   · `{ hayCanal: true, valor }` → contestó (aunque sea `undefined` por un fallo transitorio).
 */
async function intentarEspejo(url, recurso, cuerpo) {
  let resp;
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cuerpo),
    });
  } catch (_) {
    return { hayCanal: false }; // la petición no llegó
  }

  // 503 = existe pero no tiene token. 404 = no está desplegado. En ambos, este espejo no sirve
  // para esta instalación.
  if (resp.status === 503 || resp.status === 404) return { hayCanal: false };

  // 400 = el recurso no está declarado en el allowlist. Es un error de programación, no de
  // canal: los dos espejos comparten la misma lista, así que bajar de escalón daría el mismo
  // 400. Se deja rastro y se corta.
  if (resp.status === 400) {
    // eslint-disable-next-line no-console
    console.warn(`[flux] recurso "${recurso}" no está declarado en el espejo`);
    return { hayCanal: true, valor: undefined };
  }

  // 502 y demás: transitorio. El espejo EXISTE (no se descarta).
  if (!resp.ok) return { hayCanal: true, valor: undefined };

  return { hayCanal: true, valor: await resp.json() };
}

/**
 * Pide un recurso del espejo. Devuelve la respuesta CRUDA de FluxAI (cada llamador la interpreta),
 * o `undefined` cuando ningún espejo directo está disponible — distinto de una respuesta con
 * `encontrado:false`, que sí es una respuesta.
 *
 * @param {string} recurso  nombre declarado en el allowlist del espejo
 * @param {object} params   campos del recurso
 * @returns {Promise<object|undefined>}
 */
export async function porCanalDirecto(recurso, params = {}) {
  if (typeof fetch !== 'function') return undefined;
  const cuerpo = { recurso, ...params };

  // 1) Backend propio. Solo existe cuando `REACT_APP_HUB_API_BASE` está configurada.
  if (porElHubDisponible && hubApiActivo()) {
    const r = await intentarEspejo(`${hubApiBaseUrl()}/flux`, recurso, cuerpo);
    if (r.hayCanal) return r.valor;
    // 🔴 Se descarta SOLO el espejo, nunca el canal del hub entero: un 503 acá significa que
    // falta el token de FluxAI, no que el backend esté caído.
    porElHubDisponible = false;
  }

  // 2) Función serverless de Vercel — el camino de antes.
  if (directoDisponible) {
    const r = await intentarEspejo('/api/flux', recurso, cuerpo);
    if (r.hayCanal) return r.valor;
    directoDisponible = false;
  }

  return undefined;
}

/**
 * Espejo FluxAI: backend propio → función de Vercel. 
 *
 * @param {string} recurso      nombre en el allowlist del espejo
 * @param {object} params       campos del recurso
 * @param {string} [_legado] ignorado: el webhook viejo quedó desconectado
 * @returns {Promise<object|null>} respuesta cruda, o null si ningún camino sirvió
 */
export async function pedirAFlux(recurso, params = {}, _legado = null) {
  try {
    const directo = await porCanalDirecto(recurso, params);
    if (directo !== undefined) return directo;
    return null;
  } catch (_) {
    // Silencioso a propósito: el espejo es un ENRIQUECIMIENTO. El motivo real viaja en la
    // respuesta (`motivo`) y en los logs de la función para quien quiera diagnosticarlo.
    return null;
  }
}
